import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  DestroyRef,
  Inject,
  inject,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { DefaultCrudPopupHeaderIcons } from '../../utility/svg-constants';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import {
  ActionIcons,
  DropdownPopupHeaderMessage,
  ModalPopupMessages,
  ModeIcons,
  popupButtons,
  popupWidth,
  SnackBar,
  StatusCodes,
} from '../../utility/constants';
import { MaterialModule } from '../../material.module';
import { MessagePopupComponent } from '../message-popup/message-popup.component';
import { ModalService } from '../../services/modal.service';
import { CommonService } from '../../services/common.service';
import { catchError, EMPTY, Observable, throwError } from 'rxjs';
import { take } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { handleErrorMessage } from 'src/app/shared/operators/error.operator';
import { ErrorCode } from 'src/app/shared/utility/errorCode';
import { SnakbarService } from '../../services/snakbar.service';
import { HawbService } from 'src/app/features/service/hawb.service';
import { SvgIcon, SvgIconProp } from '../../models/dynamic.model';
import { StatusCodesService } from 'src/app/features/service/status-codes.service';
import { BagService } from 'src/app/features/service/bag.service';
import { FormGroup } from '@angular/forms';
import { ManifestService } from 'src/app/features/service/manifest.service';
import { RpeRuntimeBridgeService } from 'src/app/shared/services/rpe-runtime-bridge.service';
import {
  SmRouteKey,
} from 'src/app/shared/rpe/sm-rpe.constants';

@Component({
  selector: 'app-crud-popup',
  standalone: true,
  imports: [CommonModule, SvgIconComponent, MaterialModule],
  templateUrl: './crud-popup.component.html',
  styleUrl: './crud-popup.component.scss',
})
export class CrudPopupComponent implements OnInit, AfterViewInit {
  @ViewChild('componentContainer', { read: ViewContainerRef })
  componentContainer!: ViewContainerRef;
  public injectedComponentRef!: ComponentRef<any>;
  private readonly destroyRef = inject(DestroyRef);
  public crudPopupData: any;
  public crudPopupHeaderIcons = JSON.parse(
    JSON.stringify(DefaultCrudPopupHeaderIcons)
  );
  public showPopupIcons!: any;
  public popupButtons = popupButtons;
  private bulkBagData: any;
  private organizationId!: number | undefined;
  public dropdownPopupHeader: any = DropdownPopupHeaderMessage;
  public hidePagination = false;
  private groupDepartmentRoleId!: number | undefined;
  private numberRangesId!: number | undefined;
  private requestType!: string | undefined;
  private statusCodeInfoFormReadMode!: boolean;
  private isRestrictedStatusCodeType = false;
  private serviceTypeId!: string | undefined;
  private agentTypeId!: string | undefined;
  private clearanceType!: string | undefined;
  private ibcChargeCodeId!: string | undefined;
  private agentChargeCodeId!: number | undefined;
  private agentId!: number | undefined;
  private agentChargeCodeData!: any;
  private currencyCode!: string | undefined;
  private readonly errorHandler = handleErrorMessage();
  private readonly rpeRuntimeBridge = inject(RpeRuntimeBridgeService);

  constructor(
    public commonService: CommonService,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly ref: MatDialogRef<CrudPopupComponent>,
    private readonly cdref: ChangeDetectorRef,
    private readonly modalService: ModalService,
    private readonly snakbarService: SnakbarService,
    private readonly hawbService: HawbService,
    private readonly statusCodeService: StatusCodesService,
    private readonly bagService: BagService,
    private readonly manifestService: ManifestService
  ) { }

  ngOnInit(): void {
    this.crudPopupData = this.data;
    this.initializePopupData();
    this.rpeRuntimeBridge.contextRefresh$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshSmPopupHeaderIcons());
  }

  ngAfterViewInit(): void {
    this.injectComponent();
    this.cdref.detectChanges();
  }

  private initializePopupData() {
    const title = this.crudPopupData.title;
    const code = this.crudPopupData.code;

    const handlers: Record<string, () => void> = {
      [ModalPopupMessages.organizationCrudModalHeader]: () =>
        this.handleOrganization(code),

      [this.dropdownPopupHeader.group]: () =>
        this.handleGroupDeptRole(code),

      [this.dropdownPopupHeader.department]: () =>
        this.handleGroupDeptRole(code),

      [this.dropdownPopupHeader.role]: () =>
        this.handleGroupDeptRole(code),

      [ModalPopupMessages.numberRanges]: () =>
        this.handleNumberRanges(code),

      [ModalPopupMessages.StatusCode]: () =>
        this.handleStatusCode(code),

      [ModalPopupMessages.ServiceType]: () =>
        this.handleServiceType(code),

      [ModalPopupMessages.ServiceTypeGroups]: () =>
        this.handleServiceTypeGroupsAndClearanceTypeModalInit(),

      [ModalPopupMessages.AgentType]: () =>
        this.handleAgentTypeModalInit(),

      [ModalPopupMessages.ClearanceType]: () =>
        this.handleClearanceType(code),

      [ModalPopupMessages.ibcChargeCode]: () =>
        this.handleIBCChargeCode(code),

      [this.dropdownPopupHeader.agentChargeCode]: () =>
        this.handleAgentChargeCode(code),

      [ModalPopupMessages.currencyManagement]: () =>
        this.handleCurrencyConversion(code),
    };

    const handler = handlers[title];
    if (handler) handler();
  }

  private handleOrganization(code: any) {
    this.organizationId = code.organizationId;
    this.getHeaderIconsForPopup(this.organizationId);
  }

  private handleGroupDeptRole(code: any) {
    this.groupDepartmentRoleId = code.groupDepartmentRoleId;
    this.requestType = code.requestType;
    this.getHeaderIconsForPopup(this.groupDepartmentRoleId);
  }

  private handleNumberRanges(code: any) {
    this.numberRangesId = code.numberRangesId;
    this.getHeaderIconsForPopup(this.numberRangesId);
  }

  private handleStatusCode(code: any) {
    this.statusCodeService.requiredStatusCodeDataForInfoPage
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.isRestrictedStatusCodeType = !!(
          data &&
          this.crudPopupData.code.pageMode === ActionIcons.view &&
          data.statusCodeList?.[0] &&
          (data.statusCodeList[0].statusCodeType === 'block' ||
            data.statusCodeList[0].statusCodeType === 'ace')
        );
        this.getIconsForStatusCodePopup(code.statusCodeId, code.isBulk);
      });
    this.statusCodeService.statuCodeFormReadMode
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.statusCodeInfoFormReadMode = data;
        if (
          data &&
          this.crudPopupData.title === ModalPopupMessages.StatusCode
        ) {
          this.showReadModeIcons();
          this.hidePagination = false;
        }
      });
  }

  private handleServiceType(code: any) {
    this.serviceTypeId = code.serviceTypeId;
    this.getHeaderIconsForPopup(this.serviceTypeId);
  }

  private handleClearanceType(code: any) {
    this.clearanceType = code.clearanceType;
    this.getHeaderIconsForPopup(this.clearanceType);
  }

  private handleIBCChargeCode(code: any) {
    this.ibcChargeCodeId = code.ibcChargeCodeId;
    this.getHeaderIconsForPopup(this.ibcChargeCodeId);
  }

  private handleAgentChargeCode(code: any) {
    this.agentChargeCodeId = code.agentChargeCodeId;
    this.agentId = code.agentId;
    this.requestType = code.requestType;
    this.agentChargeCodeData = code.agentChargeCodeData;
    this.getHeaderIconsForPopup(this.agentChargeCodeId);
  }

  private handleCurrencyConversion(code: any) {
    this.currencyCode = code.currencyCode;
    this.getHeaderIconsForPopup(this.currencyCode);
  }
  public closePopup() {
    if (this.crudPopupData.code?.isFromNumberRangesSummaryPage) {
      this.onIconClicked(null, true);
    } else if (this.crudPopupData.code?.isFromServiceTypeSummaryPage) {
      if (this.injectedComponentRef?.instance?.handleServiceTypeClose) {
        this.injectedComponentRef.instance.handleServiceTypeClose(this.ref);
      }
    } else if (this.crudPopupData.code?.isFromSummaryPage) {
      if (this.injectedComponentRef?.instance?.handleClearanceTypeClose) {
        this.injectedComponentRef.instance.handleClearanceTypeClose(this.ref);
      }
    } else if (this.crudPopupData.code?.isFromIbcChargeCodeSummary) {
      if (this.injectedComponentRef?.instance?.handleCloseAction) {
        this.injectedComponentRef.instance.handleCloseAction(this.ref);
      }
    } else {
      this.ref.close(true);
    }
  }

  public getScreenData() {
    if (this.crudPopupData.code?.isStatusCodeAdditionalOrPodDetails || this.crudPopupData.code?.isStatusCodeAttachmentDetails) {
      return popupButtons.Save;
    } else if (
      this.crudPopupData.code?.isFromServiceTypeSummary ||
      this.crudPopupData.code?.isFromAgentTypeSummary ||
      this.crudPopupData.code?.isFromClearanceTypeSummary ||
      this.crudPopupData.code?.isFromServiceTypeSummaryPage ||
      this.crudPopupData.code?.isFromIbcChargeCodeSummary
    ) {
      return popupButtons.Close;
    } else if (this.crudPopupData.code?.isForBulkBagging) {
      return popupButtons.BulkBag;
    } else if (this.crudPopupData.code?.isForBagGateway) {
      return popupButtons.createManifest;
    } else if (
      this.crudPopupData.code?.isForCreateBag ||
      this.crudPopupData.code?.isForCreateManifest
    ) {
      return popupButtons.create;
    } else if (this.crudPopupData.code?.isForBulkManifesting) {
      return popupButtons.BulkManifest;
    } else if (this.crudPopupData.code?.isForBulkMawb) {
      return popupButtons.BulkMawb;
    } else if (this.crudPopupData.code?.isBagScanShipment) {
      return 'Confirm'
    } else if (this.crudPopupData.code?.isForBagScanInManifest) {
      return popupButtons.Confirm;
    } else {
      return popupButtons.Cancel;
    }
  }

  public injectComponent() {
    if (this.crudPopupData.injectedComponent) {
      this.componentContainer.clear();
      this.injectedComponentRef = this.componentContainer.createComponent(
        this.crudPopupData.injectedComponent
      );
      this.injectPopupData();
      this.bulkBagData = this.injectedComponentRef.instance;
    }
  }

  private injectPopupData() {
    const title = this.crudPopupData.title;
    const code = this.crudPopupData.code;
    const instance = this.injectedComponentRef.instance;

    const titleHandlers: Record<string, () => void> = {
      [ModalPopupMessages.organizationCrudModalHeader]: () => this.injectOrganizationData(instance),
      [this.dropdownPopupHeader.group]: () => this.injectGroupDepartmentRoleData(instance),
      [this.dropdownPopupHeader.department]: () => this.injectGroupDepartmentRoleData(instance),
      [this.dropdownPopupHeader.role]: () => this.injectGroupDepartmentRoleData(instance),
      [ModalPopupMessages.numberRanges]: () => this.injectNumberRangesData(instance),
      [ModalPopupMessages.StatusCode]: () => this.injectStatusCodeData(instance),
      [ModalPopupMessages.ServiceType]: () => this.injectServiceTypeData(instance),
      [ModalPopupMessages.AgentType]: () => this.injectAgentOrServiceTypeGroupData(instance),
      [ModalPopupMessages.ServiceTypeGroups]: () => this.injectAgentOrServiceTypeGroupData(instance),
      [ModalPopupMessages.ClearanceType]: () => this.injectClearanceTypeData(instance),
      [ModalPopupMessages.attachments]: () => { instance.rowData = code?.rowData; },
      [ModalPopupMessages.PodDetails]: () => { instance.rowData = code?.rowData; },
      [ModalPopupMessages.additionalDetails]: () => { instance.rowData = code?.rowData; },
      [ModalPopupMessages.ibcChargeCode]: () => this.injectIbcChargeCodeData(instance),
      [this.dropdownPopupHeader.agentChargeCode]: () => this.injectAgentChargeCodeData(instance),
      [ModalPopupMessages.currencyManagement]: () => this.injectCurrencyInfoData(instance),
    };

    const codeHandlers: Array<[() => boolean, () => void]> = [
      [() => code?.isForCreateBag, () => { instance.bagDetails = code.bagDetails; }],
      [() => code?.isForCreateManifest, () => { instance.manifestDetails = code.manifestDetails; }],
      [() => code?.isForBagScanInManifest, () => this.injectBagScanInManifestData(instance)],
      [() => code?.isForSendingInvoice, () => this.injectSendInvoiceData(instance)],
    ];

    const titleHandler = titleHandlers[title];
    if (titleHandler) {
      titleHandler();
      return;
    }

    // Execute first matching code-based handler
    for (const [condition, handler] of codeHandlers) {
      if (condition()) {
        handler();
        return;
      }
    }
  }

  private injectOrganizationData(instance: any) {
    if (this.organizationId) {
      instance.organizationId = this.organizationId;
      instance.organizationMode = ActionIcons.edit;
    } else {
      instance.organizationMode = ActionIcons.add;
    }
  }

  private injectGroupDepartmentRoleData(instance: any) {
    instance.groupDepartmentRoleId = this.groupDepartmentRoleId;
    instance.requestType = this.requestType;
  }

  private injectNumberRangesData(instance: any) {
    instance.numberRangesId = this.numberRangesId;
    instance.numberRangesData = this.crudPopupData.code;
  }

  private injectStatusCodeData(instance: any) {
    instance.pageMode = this.crudPopupData.code.pageMode;
  }

  private injectServiceTypeData(instance: any) {
    instance.serviceTypeId = this.serviceTypeId;
    instance.serviceTypesData = this.crudPopupData.code;
  }

  private injectAgentOrServiceTypeGroupData(instance: any) {
    instance.pageMode = this.crudPopupData.code.pageMode;
    instance.bulkMode = this.crudPopupData.code.bulkMode;
  }

  private injectClearanceTypeData(instance: any) {
    instance.clearanceType = this.clearanceType;
    instance.clearanceTypesData = this.crudPopupData.code;
  }

  private injectCurrencyInfoData(instance: any) {
    instance.currencyCode = this.currencyCode;
    instance.currencyInfoData = this.crudPopupData.code;
  }

  private injectIbcChargeCodeData(instance: any) {
    instance.ibcChargeCodeId = this.ibcChargeCodeId;
    instance.ibcChargeCodeData = this.crudPopupData.code;
  }

  private injectAgentChargeCodeData(instance: any) {
    instance.agentChargeCodeId = this.agentChargeCodeId;
    instance.requestType = this.requestType;
    instance.agentId = this.agentId;
    instance.agentChargeCodeData = this.agentChargeCodeData;
  }

  private injectBagScanInManifestData(instance: any) {
    instance.manifestId = this.crudPopupData.code.manifestId;
    instance.validationData = this.crudPopupData.code.validationData;
  }

  private injectSendInvoiceData(instance: any) {
    instance.selectedShipmentExts = this.crudPopupData.code.selectedShipmentExts;
    instance.selectedShipmentIds = this.crudPopupData.code.selectedShipmentIds;
    instance.accountNumber = this.crudPopupData.code.accountNumber;
  }

  public handleConfirmAction(event: string) {
    if (this.crudPopupData.code?.isForBulkBagging) {
      this.onBulkBagClicked();
    } else if (this.crudPopupData.code?.isForCreateBag) {
      this.onBagCreationClicked();
    } else if (this.crudPopupData.code?.isForCreateManifest) {
      this.onManifestCreationClicked();
    } else if (this.crudPopupData.code?.isForBulkManifesting) {
      this.handleBulkManifest();
    } else if (this.crudPopupData.code?.isForBulkMawb) {
      this.handleBulkMawb();
    } else if (this.crudPopupData.code?.isStatusCodeAttachmentDetails || this.crudPopupData.code?.isStatusCodeAdditionalOrPodDetails) {
      this.handleStatusCodeAttachmentDetails();
    } else if (this.crudPopupData.code?.isBagScanShipment) {
      this.handleBagScanShipment();
    } else if (this.crudPopupData.code?.isForBagScanInManifest) {
      this.handleScanBagInManifest();
    } else if (event == SnackBar.Close || event == popupButtons.Cancel) {
      if (
        this.crudPopupData.title ===
        ModalPopupMessages.organizationCrudModalHeader
      ) {
        this.onIconClicked(null, true);
      } else if (
        this.crudPopupData.title === this.dropdownPopupHeader.group ||
        this.crudPopupData.title === this.dropdownPopupHeader.department ||
        this.crudPopupData.title === this.dropdownPopupHeader.role
      ) {
        this.onIconClicked(null, true);
      } else {
        this.closePopup();
      }
    }
  }

  private onBulkBagClicked() {
    this.bulkBagData.bulkBagForm.markAllAsTouched();
    if (
      !this.bulkBagData.destinationError &&
      this.bulkBagData.bulkBagForm.valid
    ) {
      const dialogRef = this.modalService.openPopup(
        {
          isForBulkBagConfirmation: true,
          isFooterRequired: true,
          ...this.getMessage(),
        },
        ModalPopupMessages.bulkBag,
        MessagePopupComponent,
        popupWidth.searchPopup
      );
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(data => {
          if (data === true) {
            this.addPiecesToBag();
          }
        });
    }
  }

  private onBagCreationClicked() {
    this.bulkBagData.bulkBagForm.markAllAsTouched();
    if (
      !this.bulkBagData.destinationError &&
      this.bulkBagData.bulkBagForm.valid &&
      this.bulkBagData.bulkBagForm.value.destination
    ) {
      this.commonService
        .createOutGoingBag(this.bulkBagData.bulkBagForm.value)
        .pipe(
          catchError((err: HttpErrorResponse) => {
            if (
              err.error?.code === ErrorCode.ALREADY_EXISTS &&
              err.error?.details?.nextExt
            ) {
              this.showExistsPopup(
                this.bulkBagData.bulkBagForm.get('bagExt')?.value,
                err.error.details.nextExt,
                'Bag'
              )
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((accepted: boolean) => {
                  if (accepted) {
                    this.bulkBagData.bulkBagForm
                      .get('bagExt')
                      ?.setValue(err.error.details.nextExt);
                  }
                });
              return EMPTY;
            }
            return throwError(() => err);
          }),
          this.errorHandler(),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: (resp: any) => {
            this.commonService.hitAPIAfterCBPClearance(true);
            this.snakbarService.openCustomisedSnackBar(
              resp.message,
              ModalPopupMessages.success
            );
            this.ref.close({
              popupClose: true,
              bagData: this.bulkBagData.bulkBagForm.value,
            });
          },
        });
    }
  }

  private onManifestCreationClicked() {
    this.bulkBagData.form.markAllAsTouched();
    this.bulkBagData.form.value.manifestDate =
      this.bulkBagData.manifestDetails.manifestDate;
    if (this.bulkBagData.form.valid) {
      this.commonService
        .createOutGoingManifest(this.bulkBagData.form.value)
        .pipe(
          catchError((err: HttpErrorResponse) => {
            if (
              err.error?.code === ErrorCode.ALREADY_EXISTS &&
              err.error?.details?.nextExt
            ) {
              this.showExistsPopup(
                this.bulkBagData.form.get('manifestNumber')?.value,
                err.error.details.nextExt,
                'Manifest'
              )
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((accepted: boolean) => {
                  if (accepted) {
                    this.bulkBagData.form
                      .get('manifestNumber')
                      ?.setValue(err.error.details.nextExt);
                  }
                });
              return EMPTY;
            }
            return throwError(() => err);
          }),
          this.errorHandler(),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: (resp: any) => {
            this.snakbarService.openCustomisedSnackBar(
              resp.message,
              ModalPopupMessages.success
            );
            this.ref.close({
              popupClose: true,
              manifestData: this.bulkBagData.form.value,
            });
          },
        });
    }
  }

  private getMessage() {
    const unBaggedPiecesList = this.bulkBagData.shipmentPiecesData.filter(
      (pieces: any) => !pieces.isBagging
    );
    if (
      unBaggedPiecesList.length === this.bulkBagData.shipmentPiecesData.length
    ) {
      return {
        message: ModalPopupMessages.bulkBagConfirmationMessage(
          this.bulkBagData.shipmentPiecesData.length,
          this.bulkBagData.bulkBagForm.get('bagExt')?.value
        ),
      };
    } else {
      return {
        message: ModalPopupMessages.partialBulkBagConfirmationMessage(
          this.bulkBagData.shipmentPiecesData.length,
          this.bulkBagData.shipmentPiecesData.length -
          unBaggedPiecesList.length,
          unBaggedPiecesList.length,
          this.bulkBagData.bulkBagForm.get('bagExt')?.value
        ),
      };
    }
  }

  private addPiecesToBag() {
    const piecesList = this.bulkBagData.shipmentPiecesData
      .filter((pieces: any) => !pieces.isBagging)
      .map((shipmentPiece: any) => shipmentPiece.hawbId);
    const bulkBaggingData = {
      ids: piecesList,
      bagId: this.bulkBagData.enteredBagData.bagId,
      originId: this.bulkBagData.shipmentPiecesData[0].originId,
      destinationId: this.bulkBagData.shipmentPiecesData[0].destinationId,
      bagExt: this.bulkBagData.bulkBagForm.get('bagExt')?.value,
    };
    this.commonService
      .createBagForBulkBagging(bulkBaggingData)
      .pipe(this.errorHandler(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: any) => {
          if (resp.statusCode == StatusCodes.Success) {
            this.snakbarService.openCustomisedSnackBar(
              resp.message,
              ModalPopupMessages.success
            );
            this.hawbService.ishawbSummaryApiData(true);
            this.ref.close(true);
          }
        },
      });
  }

  public getHeaderIconsForPopup(incomingId: number | string | undefined) {
    if (
      (incomingId &&
        this.crudPopupData.title === ModalPopupMessages.numberRanges &&
        this.crudPopupData.code.mode === ModeIcons.view) ||
      (this.crudPopupData.title === ModalPopupMessages.ServiceType &&
        this.crudPopupData.code.mode === ModeIcons.view) ||
      (this.crudPopupData.title === ModalPopupMessages.ClearanceType &&
        this.crudPopupData.code.mode === ModeIcons.view) ||
      (this.crudPopupData.title === ModalPopupMessages.ibcChargeCode &&
        this.crudPopupData.code.mode === ModeIcons.view) ||
      (incomingId &&
        this.crudPopupData.title === this.dropdownPopupHeader.group) ||
      (incomingId &&
        this.crudPopupData.title === this.dropdownPopupHeader.department) ||
      (incomingId &&
        this.crudPopupData.title === this.dropdownPopupHeader.role) ||
      (incomingId &&
        this.crudPopupData.title === this.dropdownPopupHeader.agentChargeCode) ||
      (incomingId &&
        this.crudPopupData.title ===
        ModalPopupMessages.organizationCrudModalHeader) ||
      (this.crudPopupData.title === ModalPopupMessages.currencyManagement &&
        this.crudPopupData.code.mode === ModeIcons.view)
    ) {
      const filteredIconList: SvgIconProp[] = [];
      this.crudPopupHeaderIcons.forEach((val: SvgIconProp) => {

        if (this.crudPopupData.title === this.dropdownPopupHeader.agentChargeCode) {
          if (val.name === ActionIcons.edit) {
            val.isDisabled = false;
            filteredIconList.push(val);
          }
        } else if (val.name === ActionIcons.edit || val.name === ActionIcons.delete) {
          val.isDisabled = false;
          filteredIconList.push(val);
        }
        if (this.crudPopupData.title === ModalPopupMessages.numberRanges) {
          if (val.name === ActionIcons.print || val.name === ActionIcons.downloadFile) {
            val.isDisabled = false;
            filteredIconList.push(val);
          }
        }
      });
      this.showPopupIcons = this.applySmRpeInfoIcons(filteredIconList);
    } else {
      this.showSaveCloseIcons();
    }
  }

  private showSaveCloseIcons() {
    const filteredIconList: SvgIconProp[] = [];
    this.crudPopupHeaderIcons.forEach((val: SvgIconProp) => {
      if (val.name === ActionIcons.save || val.name === ActionIcons.close) {
        val.isDisabled = false;
        filteredIconList.push(val);
      }
    });
    this.showPopupIcons = filteredIconList;
  }

  private showReadModeIcons() {
    const filteredIconList: SvgIconProp[] = [];
    this.crudPopupHeaderIcons.forEach((val: SvgIconProp) => {
      if (
        val.name === ActionIcons.add ||
        val.name === ActionIcons.edit ||
        val.name === ActionIcons.delete
      ) {
        val.isDisabled = false;
        filteredIconList.push(val);
      }
    });
    this.showPopupIcons = filteredIconList;
  }

  private getSmRouteForCurrentPopup(): SmRouteKey | null {
    const title = this.crudPopupData?.title;
    const routeByTitle: Partial<Record<string, SmRouteKey>> = {
      [ModalPopupMessages.AgentType]: 'agent-types',
      [ModalPopupMessages.ServiceTypeGroups]: 'service-type-groups',
      [ModalPopupMessages.ServiceType]: 'service-types',
      [ModalPopupMessages.ClearanceType]: 'clearance-types',
      [ModalPopupMessages.numberRanges]: 'number-ranges',
      [ModalPopupMessages.StatusCode]: 'status-codes',
      [ModalPopupMessages.ibcChargeCode]: 'ibc-charge-codes',
      [ModalPopupMessages.currencyManagement]: 'currency-management',
    };
    return routeByTitle[title] ?? null;
  }

  private isBulkInfoView(): boolean {
    const code = this.crudPopupData?.code;
    if (!code) {
      return false;
    }
    return !!(
      code.isBulk ||
      code.pageMode === ModeIcons.bulkView ||
      code.mode === ModeIcons.bulkView ||
      (code.isPaginationRequired && (code.totalData ?? 0) > 1)
    );
  }

  private applySmRpeInfoIcons(icons: SvgIconProp[]): SvgIconProp[] {
    const route = this.getSmRouteForCurrentPopup();
    if (!route) {
      return icons;
    }
    return this.rpeRuntimeBridge.applySmInfoIcons(route, icons, this.isBulkInfoView());
  }

  private refreshSmPopupHeaderIcons(): void {
    const title = this.crudPopupData?.title;
    const code = this.crudPopupData?.code;
    if (
      title === ModalPopupMessages.AgentType ||
      title === ModalPopupMessages.ServiceTypeGroups
    ) {
      this.getIconsForAgentType(code?.pageMode ?? ActionIcons.view);
      return;
    }
    if (title === ModalPopupMessages.StatusCode) {
      this.getIconsForStatusCodePopup(code?.statusCodeId, code?.isBulk);
      return;
    }
    if (title === ModalPopupMessages.numberRanges) {
      this.getHeaderIconsForPopup(this.numberRangesId);
      return;
    }
    if (title === ModalPopupMessages.ServiceType) {
      this.getHeaderIconsForPopup(this.serviceTypeId);
      return;
    }
    if (title === ModalPopupMessages.ClearanceType) {
      this.getHeaderIconsForPopup(this.clearanceType);
      return;
    }
    if (title === ModalPopupMessages.ibcChargeCode) {
      this.getHeaderIconsForPopup(this.ibcChargeCodeId);
      return;
    }
    if (title === ModalPopupMessages.currencyManagement) {
      this.getHeaderIconsForPopup(this.currencyCode);
    }
  }

  public getIconsForGroupDepartmentPopup(
    groupDepartmentRoleId: number | undefined
  ) {
    if (groupDepartmentRoleId) {
      const filteredIconList: SvgIconProp[] = [];
      this.crudPopupHeaderIcons.forEach((val: SvgIconProp) => {
        if (val.name === ActionIcons.edit || val.name === ActionIcons.delete) {
          val.isDisabled = false;
          filteredIconList.push(val);
        }
      });
      this.showPopupIcons = filteredIconList;
    } else {
      this.showSaveCloseIcons();
    }
  }

  public getIconsForStatusCodePopup(
    statusCodeId: number | undefined,
    isBulk?: boolean
  ) {
    if (statusCodeId) {
      const filteredIconList: SvgIconProp[] = [];
      this.crudPopupHeaderIcons.forEach((val: SvgIconProp) => {
        if (val.name === ActionIcons.edit) {
          val.isDisabled = false;
          filteredIconList.push(val);
        } else if (
          val.name === ActionIcons.delete &&
          !this.identifyStatusCodeType()
        ) {
          filteredIconList.push(val);
        } else if (val.name === ActionIcons.add) {
          val.isDisabled = false;
          if (!isBulk) {
            filteredIconList.push(val);
          }
        }
      });
      this.showPopupIcons = this.applySmRpeInfoIcons(filteredIconList);
    } else {
      this.showSaveCloseIcons();
    }
  }

  public onIconClicked(clickedIcon?: SvgIcon | null, isClose?: boolean) {
    if (
      this.crudPopupData.title ===
      ModalPopupMessages.organizationCrudModalHeader
    ) {
      this.onOrganizationIconClicked(clickedIcon, isClose);
    } else if (
      this.crudPopupData.title === this.dropdownPopupHeader.group ||
      this.crudPopupData.title === this.dropdownPopupHeader.department ||
      this.crudPopupData.title === this.dropdownPopupHeader.role
    ) {
      this.onGroupDepartmentRoleIconClicked(clickedIcon, isClose);
    } else if (
      this.crudPopupData.title === this.dropdownPopupHeader.agentChargeCode
    ) {
      this.onAgentChargeCodeIconClicked(clickedIcon, isClose);
    } else if (this.crudPopupData.title === ModalPopupMessages.numberRanges) {
      this.onNumberRangesIconClicked(clickedIcon, isClose);
    } else if (this.crudPopupData.title === ModalPopupMessages.StatusCode) {
      this.onStatusCodeIconClicked(clickedIcon, isClose);
    } else if (
      this.crudPopupData.title === ModalPopupMessages.AgentType ||
      this.crudPopupData.title === ModalPopupMessages.ServiceTypeGroups
    ) {
      this.triggerAgentTypeAction(
        isClose ? ActionIcons.close : (clickedIcon?.name as string)
      );
    } else if (this.crudPopupData.title === ModalPopupMessages.ServiceType) {
      this.onServiceTypeIconClicked(clickedIcon);
    } else if (this.crudPopupData.title === ModalPopupMessages.ClearanceType) {
      this.onClearanceTypeGroupIconClicked(clickedIcon);
    } else if (this.crudPopupData.title === ModalPopupMessages.ibcChargeCode) {
      this.onIbcChargeCodeInfoIconClicked(clickedIcon);
    } else if (this.crudPopupData.title === ModalPopupMessages.currencyManagement) {
      this.tiggerCurrencyInfoIconClicked(clickedIcon);
    }
  }

  public getIconsForAgentType(pageMode: string) {
    const headerIconList: SvgIconProp[] = [];
    if ([ModeIcons.bulkView, ActionIcons.view].includes(pageMode)) {
      this.crudPopupHeaderIcons.forEach((icon: SvgIconProp) => {
        if (
          icon.name === ActionIcons.edit ||
          icon.name === ActionIcons.delete
        ) {
          icon.isDisabled = false;
          headerIconList.push(icon);
        }
      });
      this.showPopupIcons = this.applySmRpeInfoIcons(headerIconList);
    } else {
      this.showSaveCloseIcons();
    }
  }

  private onOrganizationIconClicked(
    clickedIcon?: SvgIcon | null,
    isClose?: boolean
  ) {
    if (clickedIcon?.name === ActionIcons.edit) {
      this.showSaveCloseIcons();
      this.sendActionToOrganization(ActionIcons.edit);
    } else if (clickedIcon?.name === ActionIcons.delete) {
      this.sendActionToOrganization(ActionIcons.delete);
    } else if (clickedIcon?.name === ActionIcons.save) {
      this.sendActionToOrganization(ActionIcons.save);
    } else if (clickedIcon?.name === ActionIcons.close) {
      this.sendActionToOrganization(ActionIcons.close);
    } else if (isClose) {
      this.sendActionToOrganization(popupButtons.Cancel);
    }
  }

  private onGroupDepartmentRoleIconClicked(
    clickedIcon?: SvgIcon | null,
    isClose?: boolean
  ) {
    if (clickedIcon?.name === ActionIcons.edit) {
      this.showSaveCloseIcons();
      this.sendActionToGroupDepartmentRole(ActionIcons.edit);
    } else if (clickedIcon?.name === ActionIcons.delete) {
      this.sendActionToGroupDepartmentRole(ActionIcons.delete);
    } else if (clickedIcon?.name === ActionIcons.save) {
      this.sendActionToGroupDepartmentRole(ActionIcons.save);
    } else if (clickedIcon?.name === ActionIcons.close) {
      this.sendActionToGroupDepartmentRole(ActionIcons.close);
    } else if (isClose) {
      this.sendActionToGroupDepartmentRole(popupButtons.Cancel);
    }
  }

  private onNumberRangesIconClicked(
    clickedIcon?: SvgIcon | null,
    isClose?: boolean
  ) {
    if (clickedIcon?.name === ActionIcons.edit) {
      this.showSaveCloseIcons();
      this.sendActionToNumberRanges(ActionIcons.edit);
      this.hidePagination = true;
    } else if (clickedIcon?.name === ActionIcons.delete) {
      this.sendActionToNumberRanges(ActionIcons.delete);
    } else if (clickedIcon?.name === ActionIcons.save) {
      this.sendActionToNumberRanges(ActionIcons.save);
    } else if (clickedIcon?.name === ActionIcons.close) {
      this.sendActionToNumberRanges(ActionIcons.close);
    } else if (isClose) {
      this.sendActionToNumberRanges(popupButtons.Cancel);
    } else if (clickedIcon?.name === ActionIcons.print) {
      this.sendActionToNumberRanges(ActionIcons.print);
    } else if (clickedIcon?.name === ActionIcons.downloadFile) {
      this.sendActionToNumberRanges(ActionIcons.downloadFile);
    }
  }

  private sendActionToOrganization(action: string) {
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.onActionIcons(action, this.ref);
    }
  }

  private sendActionToGroupDepartmentRole(action: string) {
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.onGroupDepartmentRoleActionIcons(
        action,
        this.ref
      );
    }
  }

  private onAgentChargeCodeIconClicked(
    clickedIcon?: SvgIcon | null,
    isClose?: boolean
  ) {
    if (clickedIcon?.name === ActionIcons.edit) {
      this.showSaveCloseIcons();
      this.sendActionToAgentChargeCode(ActionIcons.edit);
    } else if (clickedIcon?.name === ActionIcons.save) {
      this.sendActionToAgentChargeCode(ActionIcons.save);
    } else if (clickedIcon?.name === ActionIcons.close) {
      this.sendActionToAgentChargeCode(ActionIcons.close);
    } else if (isClose) {
      this.sendActionToAgentChargeCode(popupButtons.Cancel);
    }
  }

  private sendActionToAgentChargeCode(action: string) {
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.onAgentChargeCodeActionIcons(
        action,
        this.ref
      );
    }
  }

  private sendActionToNumberRanges(action: string) {
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.onNumberRangesActionIcons(
        action,
        this.ref
      );
    }
  }

  private sendActionToStatusCode(action: string, pageMode?: string) {
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.onStatusCodeActions(
        action,
        this.ref,
        pageMode
      );
    }
  }

  private sendActionToAgentType(action: string) {
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.onAgentTypeActions(action, this.ref);
    }
  }

  private sendActionToServiceType(action: string) {
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.onServiceTypeHeaderActions(
        action,
        this.ref
      );
    }
  }

  private triggerAgentTypeAction(action: string) {
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.onAction(action, this.ref);
      if (action === ActionIcons.edit) {
        this.hidePagination = true; //disabling the pagination icon when form is in edit mode
      }
    }
  }

  private sendActionToClearanceTypeGroup(action: string) {
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.onClearanceTypeGroupHeaderActions(
        action,
        this.ref
      );
    }
  }

  private handleActionToIbcChargeCode(action: string) {
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.onIbcChargeCodeInfoHeaderActions(
        action,
        this.ref
      );
    }
  }
  private handleActionToCurrencyInfoPage(action: string) {
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.onCurrencyInfoHeaderActions(
        action,
        this.ref
      );
    }
  }
  private onStatusCodeIconClicked(
    clickedIcon?: SvgIcon | null,
    isClose?: boolean
  ) {
    if (clickedIcon?.name === ActionIcons.edit) {
      this.showSaveCloseIcons();
      this.sendActionToStatusCode(ActionIcons.edit);
      this.hidePagination = true;
      this.injectedComponentRef.instance.pageMode = ActionIcons.edit;
    } else if (clickedIcon?.name === ActionIcons.delete) {
      this.sendActionToStatusCode(ActionIcons.delete);
    } else if (clickedIcon?.name === ActionIcons.save) {
      this.sendActionToStatusCode(
        ActionIcons.save,
        this.crudPopupData.code.pageMode
      );
    } else if (clickedIcon?.name === ActionIcons.close) {
      this.sendActionToStatusCode(ActionIcons.close);
      this.hidePagination = false;
    } else if (isClose) {
      this.sendActionToStatusCode(popupButtons.Cancel);
    } else if (clickedIcon?.name === ActionIcons.add) {
      this.sendActionToStatusCode(ActionIcons.add);
      this.showSaveCloseIcons();
    }
  }

  private onAgentTypeIconClicked(clickedIcon?: SvgIcon | null) {
    if (clickedIcon?.name === ActionIcons.save) {
      this.sendActionToAgentType(ActionIcons.save);
    } else if (clickedIcon?.name === ActionIcons.close) {
      this.sendActionToAgentType(ActionIcons.close);
    }
  }

  private onServiceTypeIconClicked(clickedIcon?: SvgIcon | null) {
    if (clickedIcon?.name === ActionIcons.save) {
      this.sendActionToServiceType(ActionIcons.save);
    } else if (clickedIcon?.name === ActionIcons.close) {
      this.sendActionToServiceType(ActionIcons.close);
    } else if (clickedIcon?.name === ActionIcons.edit) {
      this.showSaveCloseIcons();
      this.sendActionToServiceType(ActionIcons.edit);
      this.hidePagination = true;
    } else if (clickedIcon?.name === ActionIcons.delete) {
      this.sendActionToServiceType(ActionIcons.delete);
    }
  }

  private onClearanceTypeGroupIconClicked(clickedIcon?: SvgIcon | null) {
    if (clickedIcon?.name === ActionIcons.save) {
      this.sendActionToClearanceTypeGroup(ActionIcons.save);
    } else if (clickedIcon?.name === ActionIcons.close) {
      this.sendActionToClearanceTypeGroup(ActionIcons.close);
    } else if (clickedIcon?.name === ActionIcons.edit) {
      this.showSaveCloseIcons();
      this.sendActionToClearanceTypeGroup(ActionIcons.edit);
      this.hidePagination = true;
    } else if (clickedIcon?.name === ActionIcons.delete) {
      this.sendActionToClearanceTypeGroup(ActionIcons.delete);
    }
  }

  private onIbcChargeCodeInfoIconClicked(clickedIcon?: SvgIcon | null) {
    if (clickedIcon?.name === ActionIcons.save) {
      this.handleActionToIbcChargeCode(ActionIcons.save);
    } else if (clickedIcon?.name === ActionIcons.close) {
      this.handleActionToIbcChargeCode(ActionIcons.close);
    } else if (clickedIcon?.name === ActionIcons.edit) {
      this.showSaveCloseIcons();
      this.handleActionToIbcChargeCode(ActionIcons.edit);
      this.hidePagination = true;
    } else if (clickedIcon?.name === ActionIcons.delete) {
      this.handleActionToIbcChargeCode(ActionIcons.delete);
    }
  }

  private tiggerCurrencyInfoIconClicked(iconName?: SvgIcon | null) {
    if (iconName?.name === ActionIcons.save) {
      this.handleActionToCurrencyInfoPage(ActionIcons.save);
    } else if (iconName?.name === ActionIcons.close) {
      this.handleActionToCurrencyInfoPage(ActionIcons.close);
    } else if (iconName?.name === ActionIcons.edit) {
      this.showSaveCloseIcons();
      this.handleActionToCurrencyInfoPage(ActionIcons.edit);
      this.hidePagination = true;
    } else if (iconName?.name === ActionIcons.delete) {
      this.handleActionToCurrencyInfoPage(ActionIcons.delete);
    }
  }
  private identifyStatusCodeType(): boolean {
    return this.isRestrictedStatusCodeType;
  }

  public onChangePage(event: any) {
    const previousIndex = this.crudPopupData.code?.recordIndex ?? 0; // Store the previous index
    const currentIndex = event.pageIndex; // Get new index after clicking
    const direction = currentIndex > previousIndex ? 'next' : 'prev';
    this.crudPopupData.code.recordIndex = currentIndex;
    this.injectedComponentRef.instance.paginationDirection(
      direction,
      previousIndex,
      currentIndex
    );
  }

  private handleServiceTypeGroupsAndClearanceTypeModalInit() {
    this.getIconsForAgentType(this.crudPopupData.code.pageMode);
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.pageMode =
        this.crudPopupData.code.pageMode;
    }
  }

  private handleAgentTypeModalInit() {
    this.agentTypeId = this.crudPopupData.code.agentTypeId;
    this.getIconsForAgentType(this.crudPopupData.code.pageMode);
    if (this.injectedComponentRef) {
      this.injectedComponentRef.instance.pageMode =
        this.crudPopupData.code.pageMode;
    }
  }
  private handleBulkManifest() {
    this.bulkBagData.manifestFormGroup.markAllAsTouched();
    if (
      !this.bulkBagData.destinationErrorVal &&
      this.bulkBagData.manifestFormGroup.valid
    ) {
      const dialogRef = this.modalService.openPopup(
        {
          isBulkManifestconfirmation: true,
          isFooterRequired: true,
          ...this.getConfirmationMessagrForBulkManifest(),
        },
        ModalPopupMessages.bulkManifest,
        MessagePopupComponent,
        popupWidth.searchPopup
      );
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(data => {
          if (data === true) {
            this.createBulkManifest();
          }
        });
    }
  }
  private getConfirmationMessagrForBulkManifest() {
    const unManifestList = this.bulkBagData.selectedBagsData.filter(
      (item: any) => (item.manId == null)
    );
    if (
      unManifestList.length === this.bulkBagData.selectedBagsData.length
    ) {
      return {
        message: ModalPopupMessages.bulkManifestingConfirmationMessage(
          this.bulkBagData.selectedBagsData.length,
          this.bulkBagData.manifestFormGroup.get('manifestNumber')?.value
        ),
      };
    } else {
      return {
        message: ModalPopupMessages.partialBulkManifestConfirmationMsg(
          this.bulkBagData.selectedBagsData.length,
          this.bulkBagData.selectedBagsData.length -
          unManifestList.length,
          unManifestList.length,
          this.bulkBagData.manifestFormGroup.get('manifestNumber')?.value
        ),
      };
    }
  }
  private createBulkManifest() {
    const bagItemList = this.bulkBagData.selectedBagsData
      .filter((item: any) => item?.manId == null)
      .map((bagDetail: any) => bagDetail.id);
    const bulkManiData = {
      ids: bagItemList,
      manId: this.bulkBagData.manifestData.manifestId,
      originId: this.bulkBagData.selectedBagsData[0].originId,
      destinationId: this.bulkBagData.selectedBagsData[0].destinationId,
      manifestNumber: this.bulkBagData.manifestFormGroup.get('manifestNumber')?.value,
    };
    this.manifestService
      .createManifestForBulkManifesting(bulkManiData)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: any) => {
          if (resp.statusCode == StatusCodes.Success) {
            this.snakbarService.openCustomisedSnackBar(
              resp.message,
              ModalPopupMessages.success
            );
            this.bagService.isBagSummaryPrevSearch('');
            this.bagService.isbagSummaryApiData(true);
            this.ref.close(true);
          }
        },
      });
  }


  private showExistsPopup(existingNumber: string, suggestedNumber: string, module: string): Observable<boolean> {
    const dialogRef = this.modalService.openPopup(
      {
        message: `The entered ${existingNumber} already exists for the ${module}. Would you like to use the next available number ${suggestedNumber}?`,
        isMultiple: false,
        isFooterRequired: true,
        isForNumberRange: true,
        buttonName: `Proceed with ${suggestedNumber}`,
      },
      ModalPopupMessages.numberRangeConfirmationText,
      MessagePopupComponent,
      popupWidth.deletePopup
    );
    return dialogRef.afterClosed();
  }

  handleBulkMawb() {
    this.bulkBagData.mawbFormGroup.markAllAsTouched();
    if (this.bulkBagData.mawbFormGroup.valid) {
      const dialogRef = this.modalService.openPopup(
        {
          isBulkMawbConfirmation: true,
          isFooterRequired: true,
          ...this.getConfirmationMessagrForBulkMawb(),
        },
        ModalPopupMessages.bulkMawb,
        MessagePopupComponent,
        popupWidth.searchPopup
      );
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(data => {
          if (data === true) {
            this.createBulkMawb();
          }
        });
    }
  }

  private getConfirmationMessagrForBulkMawb() {
    const unMawbedList = this.bulkBagData.selectedmawbData
      .filter(
        (item: any) => item.mawbId == null
      );
    if (
      unMawbedList?.length === this.bulkBagData.selectedmawbData.length
    ) {
      return {
        message: ModalPopupMessages.bulkMAWBConfirmationMessage(
          this.bulkBagData.selectedmawbData.length,
          this.bulkBagData.mawbFormGroup.get('mawbExt')?.value
        ),
      };
    } else {
      return {
        message: ModalPopupMessages.partialBulkMawbedConfirmationMsg(
          this.bulkBagData.selectedmawbData.length,
          this.bulkBagData.selectedmawbData.length -
          unMawbedList.length,
          unMawbedList.length,
          this.bulkBagData.mawbFormGroup.get('mawbExt')?.value
        ),
      };
    }
  }
  private createBulkMawb() {
    const manifestList = this.bulkBagData.selectedmawbData
      .filter((item: any) => (item?.mawbId == null))
      .map((bagDetail: any) => bagDetail.id);
    const bulkMawbData = {
      ids: manifestList,
      manId: 0,//this.bulkBagData.mawbData.manifestId,
      originId: this.bulkBagData.selectedmawbData[0].originId,
      destinationId: this.bulkBagData.selectedmawbData[0].destinationId,
      mawbExt: this.bulkBagData.mawbFormGroup.get('mawbExt')?.value,
    };
    this.commonService
      .createMAWBForBulk(bulkMawbData)
      .pipe(this.errorHandler(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (resp: any) => {
          if (resp.status == StatusCodes.Success) {
            this.snakbarService.openCustomisedSnackBar(
              resp.message,
              ModalPopupMessages.success
            );
            this.manifestService.isManifestSummaryApiData(true);
            this.ref.close(true);
          }
        },
      });
  }

  handleStatusCodeAttachmentDetails() {
    const formGroup = this.bulkBagData?.form || this.bulkBagData?.createAttchementForm;
    if (formGroup instanceof FormGroup) {
      formGroup.markAllAsTouched();
    }
    if (formGroup.valid) {
      this.ref.close(this.bulkBagData);
    }
  }
  private handleBagScanShipment() {
    const shipmentList = this.bulkBagData.dataSource
      .map((ship: any) => ship.id);
    const scanShipmentPayload = {
      shipmentIds: shipmentList,
      bagId: this.bulkBagData?.selectedBagsScanData[0]?.id
    };
    this.commonService
      .createBagShipmentFromSummary(scanShipmentPayload)
      .pipe(this.errorHandler(), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result: any) => {
          if (result.statusCode == StatusCodes.Success) {
            this.snakbarService.openCustomisedSnackBar(
              result.message,
              ModalPopupMessages.success
            );
            this.ref.close(true);
            this.bagService.isbagSummaryApiData(true);
            this.bagService.isBagSummaryPrevSearch('');
          }
        },
      });
  }
  private handleScanBagInManifest() {
    const scannedBags = this.bulkBagData?.scannedBagsTableData || [];
    const validationData = this.bulkBagData?.getValidationData();

    if (!validationData || scannedBags.length === 0) {
      return;
    }

    const bagIds = scannedBags
      .map((bag: any) => bag.bagId)
      .filter((id: number) => id !== undefined && id !== null);

    const payload = {
      ids: bagIds,
      manId: Number(validationData.manifestId),
      originId: validationData.origin,
      destinationId: validationData.destination,
      manifestNumber: validationData.manifestExt
    };

    this.manifestService
      .createManifestForBulkManifesting(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          if (response.statusCode === StatusCodes.Success) {
            this.snakbarService.openCustomisedSnackBar(
              ModalPopupMessages.scannedBagsAddedSuccess,
              ModalPopupMessages.success
            );
            this.ref.close({ popupClose: true });
          }
        }
      });
  }

  public isConfirmButtonDisabled(): boolean {
    if (this.crudPopupData.code?.isForBagScanInManifest) {
      const scannedBags = this.bulkBagData?.scannedBagsTableData || [];
      return scannedBags.length === 0;
    }
    // Attachment/POD popups upload to blob storage and expose `isUploading`.
    if (
      this.crudPopupData.code?.isStatusCodeAttachmentDetails ||
      this.crudPopupData.code?.isStatusCodeAdditionalOrPodDetails
    ) {
      if (this.bulkBagData?.isUploading) {
        return true;
      }
      // The attachment popup has its own form and no driver/carrier.
      if (this.crudPopupData.code?.isStatusCodeAttachmentDetails) {
        const attachmentForm = this.bulkBagData?.createAttchementForm;
        if (!attachmentForm || attachmentForm.invalid) {
          return true;
        }
        // Empty form: nothing to save, whether or not a field is required.
        return !Object.values(attachmentForm.getRawValue()).some(value =>
          String(value ?? '').trim()
        );
      }
      const form = this.bulkBagData?.form;
      // A disabled form is a saved row in view mode: nothing to save.
      if (!form || form.disabled || form.invalid) {
        return true;
      }
      return !Object.values(form.getRawValue()).some(value =>
        String(value ?? '').trim()
      );
    }
    return false;
  }
}
